import React, { useState } from 'react';
import { Linkedin, Mail, Globe } from 'lucide-react';
import { motion } from 'framer-motion';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [logoError, setLogoError] = useState(false);

  // Generate footer-specific particles
  const footerParticles = Array.from({ length: 10 }, (_, i) => ({
    id: i,
    size: Math.random() * 2.5 + 1.5,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 8 + 10,
    delay: Math.random() * 2,
  }));

  return (
    <footer className="py-12 relative bg-primary/80">
      {/* Footer-specific particles */}
      {footerParticles.map((particle) => (
        <motion.div
          key={`footer-particle-${particle.id}`}
          className="absolute rounded-full bg-accent/30 pointer-events-none"
          style={{
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            boxShadow: `0 0 ${particle.size * 2}px rgba(73, 17, 28, 0.5)`,
            zIndex: 1,
          }}
          animate={{
            x: [0, Math.random() * 40 - 20, Math.random() * -40 + 20, 0],
            y: [0, Math.random() * 40 - 20, Math.random() * -40 + 20, 0],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <div>
            <div className="mb-4">
              {logoError ? (
                <h1 className="font-['Bodoni_Moda'] text-2xl md:text-3xl font-bold text-neutral tracking-wider">
                  <span className="text-[1.3em]">Q</span>UANTUM<span className="text-[1.3em]">S</span>PHERE
                </h1>
              ) : (
                <img 
                  src="/logo.svg" 
                  alt="QuantumSphere Logo" 
                  className="h-8" 
                  onError={() => setLogoError(true)}
                />
              )}
            </div>
            <p className="text-neutral/70 mb-3 text-sm leading-relaxed">
              Empowering clients to thrive in highly dynamic markets through forward-thinking solutions in Blockchain Innovation, Multi-sector Technology, and Strategic Growth.
            </p>
            <div className="flex flex-col space-y-1.5">
              <a 
                href="https://www.linkedin.com/company/thequantumsphere/posts/?feedView=all" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-1.5 text-neutral/70 hover:text-accent transition-colors duration-300 py-0.5"
              >
                <Linkedin className="w-5 h-5 text-accent group-hover:text-accent/80 transition-colors duration-300" />
                <span>LinkedIn</span>
              </a>
              <a 
                href="mailto:info@quantumsphere.ae" 
                className="inline-flex items-center space-x-1.5 text-neutral/70 hover:text-accent transition-colors duration-300 py-0.5"
              >
                <Mail className="w-5 h-5 text-accent group-hover:text-accent/80 transition-colors duration-300" />
                <span>info@quantumsphere.ae</span>
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold text-neutral mb-4 pb-2 relative">
              Quick Links
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-accent"></span>
            </h3>
            <ul className="grid grid-cols-1 gap-1.5">
              <li>
                <a href="#about" className="text-neutral/70 hover:text-accent transition-all duration-300 transform group-hover:translate-x-1 py-0.5">
                  About Us
                </a>
              </li>
              <li>
                <a href="#expertise" className="text-neutral/70 hover:text-accent transition-all duration-300 transform group-hover:translate-x-2">
                  Our Expertise
                </a>
              </li>
              <li>
                <a href="#founder" className="text-neutral/70 hover:text-accent transition-all duration-300 transform group-hover:translate-x-2">
                  Our Founder
                </a>
              </li>
              <li>
                <a href="#contact" className="text-neutral/70 hover:text-accent transition-all duration-300 transform group-hover:translate-x-2">
                  Contact Us
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-8 pt-4 flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
          <p className="text-neutral/60 text-sm">
            &copy; {currentYear} QuantumSphere. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;